import type { DesignerAllTexts } from '../texts.js';

/**
 * Every word the phone shell says, in Serbian (Latin).
 *
 * The rule from the designer's own `texts.ts` holds here: no component builds
 * a sentence by concatenation and no component hard-codes Serbian. A shop
 * overrides through `options.texts`, which reaches us as `ctx.texts`; the
 * handful of strings that already exist there (Gotovo, Otkaži, Vrati…) win
 * over our defaults, which is what `phoneTexts` below is for.
 *
 * `PHONE_TEXT_TABLE` is the same table with `ph`-prefixed keys so the desktop
 * agent can merge it into `DESIGNER_EXTRA_TEXTS` without a single collision
 * (`size` here is a sub-panel title, `size` there is a rail label).
 */
export const PHONE_TEXTS = {
  // ── Top bar ──────────────────────────────────────────────────────────────
  undo: 'Vrati',
  redo: 'Ponovi',
  more: 'Još opcija',
  done: 'Gotovo',
  next: 'Dalje',
  nextTo: 'Dalje: {name}',
  optionsTitle: 'Opcije proizvoda',
  cancel: 'Otkaži',
  apply: 'Primeni',
  close: 'Zatvori',
  closeDesigner: 'Zatvori dizajner',
  saved: 'Sačuvano',
  saving: 'Čuvam…',
  sidePick: 'Izaberi stranu proizvoda',
  sideTitle: 'Strana proizvoda',
  problems: 'Proveri greške',

  // ── Property grid ────────────────────────────────────────────────────────
  add: 'Dodaj',
  preview: 'Pregled',
  gridLabel: 'Svojstva elementa',
  delete: 'Obriši',
  duplicate: 'Kopiraj',
  edit: 'Uredi',
  font: 'Font',
  size: 'Veličina',
  color: 'Boja',
  align: 'Poravnanje',
  textWidth: 'Širina',
  spacing: 'Razmak',
  lineHeight: 'Visina',
  curve: 'Krivina',
  orientation: 'Orijentacija',
  rotate: 'Rotiraj',
  flip: 'Obrni',
  arrange: 'Raspored',
  pattern: 'Šare',
  textStroke: 'Okvir slova',
  shadow: 'Senka',
  opacity: 'Providnost',
  replace: 'Zameni',
  crop: 'Iseci',
  fitZone: 'Uklopi',
  fillZone: 'Popuni',
  filters: 'Filteri',
  cutout: 'Ukloni pozadinu',
  shapeFill: 'Ispuna',
  shapeLine: 'Linija',
  shapeWidth: 'Debljina',
  recolor: 'Boja',
  qrEdit: 'Uredi',
  layers: 'Slojevi',
  background: 'Pozadina',
  templates: 'Šabloni',
  nothingSelected: 'Izaberi element na dizajnu ili dodaj novi.',
  notInteractive: 'Ovaj element je deo dizajna i ne može se menjati.',
  templatesLeft: 'Još niste izmenili sve šablonske elemente.',

  // ── Sub-panels ───────────────────────────────────────────────────────────
  less: 'Manje',
  moreStep: 'Više',
  titleSize: 'Promeni veličinu slova',
  titleColor: 'Promeni boju',
  titleAlign: 'Promeni poravnanje',
  titleTextWidth: 'Širina okvira',
  titleSpacing: 'Promeni razmak slova',
  titleLineHeight: 'Promeni visinu reda',
  titleCurve: 'Promeni krivinu',
  titleRotate: 'Promeni ugao',
  titleFlip: 'Obrni element',
  titleArrange: 'Promeni raspored',
  titlePattern: 'Promeni šaru',
  titleStroke: 'Okvir slova',
  titleShadow: 'Senka',
  titleOpacity: 'Promeni providnost',
  titleEditText: 'Uredi tekst',
  titleFont: 'Promeni font',
  titleCrop: 'Iseci sliku',
  titleFilters: 'Filteri',
  titleShapeFill: 'Boja ispune',
  titleShapeLine: 'Boja linije',
  titleShapeWidth: 'Debljina linije',
  titleRecolor: 'Preboj ukrasa',
  titleQr: 'Uredi QR kod',
  enabled: 'Uključeno',
  strokeColor: 'Boja okvira',
  strokeThickness: 'Debljina',
  shadowColor: 'Boja senke',
  shadowDirection: 'Smer',
  shadowOffset: 'Razmak',
  shadowBlur: 'Zamućenje',
  cropZoom: 'Uvećanje',
  cropStraighten: 'Ispravi',
  cropReset: 'Poništi isecanje',
  filterNone: 'Bez filtera',
  filterGray: 'Crno-belo',
  filterSepia: 'Sepija',
  brightness: 'Osvetljenje',
  contrast: 'Kontrast',
  patternNone: 'Bez',
  patternBasic: 'Osnovno',
  patternCentered: 'Centrirano',
  patternHalfBrick: 'Pola cigle',
  patternHalfDrop: 'Pola kapi',
  patternMirror: 'Ogledalo',
  patternGapX: 'Razmak vodoravno',
  patternGapY: 'Razmak uspravno',
  horizontal: 'Vodoravno',
  vertical: 'Uspravno',
  stepForward: 'Korak napred',
  stepBackward: 'Korak nazad',
  alignLeft: 'Levo',
  alignCenter: 'Sredina',
  alignRight: 'Desno',
  anchorRow: 'Sidro',
  anchorTop: 'Gore',
  anchorMiddle: 'Sredina',
  anchorBottom: 'Dole',
  fixedWidth: 'Fiksna širina',
  fixedWidthOn: 'Tekst se prelama na širinu okvira.',
  fixedWidthOff: 'Okvir prati dužinu teksta.',
  wrapWidth: 'Širina okvira',

  // ── Add drawer ───────────────────────────────────────────────────────────
  addTitle: 'Dodaj u dizajn',
  tabImages: 'Slike',
  tabText: 'Tekst',
  tabElements: 'Elementi',
  tabIcons: 'Ikone',
  tabEmoji: 'Emoji',
  tabBackground: 'Pozadina',
  tabTemplates: 'Šabloni',
  tabLayers: 'Slojevi',
  tabSticker: 'Nalepnica',
  addTextButton: 'Dodaj tekst',
  titleAddText: 'Dodaj tekst',
  addQrButton: 'Dodaj QR kod',
  qrPlaceholder: 'https://',
  qrHint: 'Proverite kod kamerom telefona pre porudžbine.',
  panelMissing: 'Ovaj alat još nije dostupan na telefonu.',

  // ── More (⋯) sheet ───────────────────────────────────────────────────────
  moreTitle: 'Podešavanja',
  snap: 'Privlačenje',
  snapNote: 'Elementi se privlače uz vodiče i mrežu',
  guidelines: 'Prikaži vodiče',
  guidelinesNote: 'Linije za sečenje, vidljivi deo i bezbednu zonu',
  lockAspect: 'Zaključaj proporcije',
  lockAspectNote: 'Elementi zadržavaju odnos stranica',
  gridLines: 'Prikaži mrežu',
  gridLinesNote: 'Mreža preko platna',
  transparency: 'Prikaži providnost',
  transparencyNote: 'Šahovska podloga umesto boje',
  darkMode: 'Tamni režim',
  darkModeNote: 'Tamna podloga radnog prostora',
  shortcuts: 'Prečice na tastaturi',
  shortcutsBody: 'Ctrl+Z vraća, Shift+Ctrl+Z ponavlja, Ctrl+D kopira, Delete briše, strelice pomeraju za 1 mm.',

  // ── Review sheet ─────────────────────────────────────────────────────────
  reviewTitle: 'Pregled',
  reviewIntro: 'Proverite dizajn pre nego što nastavite.',
  reviewCheckNames: 'Proverite imena i datume',
  reviewCheckCut: 'Ništa nije odsečeno',
  reviewCheckTemplates: 'Svi šablonski elementi su izmenjeni',
  reviewCheckSides: 'Proverite sve strane',
  reviewRendering: 'Pravim prikaz…',
  reviewFailed: 'Prikaz za štampu nije dostupan.',
  reviewFix: 'Ispravi',

  // ── Long-press sheet ─────────────────────────────────────────────────────
  contextTitle: 'Element',
  copy: 'Kopiraj',
  paste: 'Nalepi',
  forward: 'Napred',
  backward: 'Nazad',
} as const;

/** Mutable on purpose: `phoneTexts` copies the table and lays overrides on top. */
export type PhoneTexts = { -readonly [K in keyof typeof PHONE_TEXTS]: string };

/**
 * The phone table with the shop's own designer strings on top.
 *
 * Only keys that mean exactly the same thing are taken over — "Gotovo" is the
 * same button on both shells, but our `size` is a sub-panel title while the
 * designer's `size` is a rail label, so that one is NOT inherited.
 */
export function phoneTexts(texts: DesignerAllTexts): PhoneTexts {
  const merged: PhoneTexts = { ...PHONE_TEXTS };
  const take = (key: keyof PhoneTexts, value: string | undefined): void => {
    if (typeof value === 'string' && value.trim()) merged[key] = value;
  };
  take('done', texts.done);
  take('cancel', texts.cancel);
  take('close', texts.close);
  take('undo', texts.undo);
  take('redo', texts.redo);
  take('delete', texts.remove);
  take('duplicate', texts.duplicate);
  take('font', texts.font);
  take('color', texts.color);
  take('align', texts.align);
  take('spacing', texts.spacing);
  take('opacity', texts.opacity);
  take('flip', texts.flip);
  take('curve', texts.bend);
  take('shadow', texts.shadow);
  take('layers', texts.layers);
  take('crop', texts.crop);
  take('cutout', texts.removeBackground);
  take('filters', texts.filters);
  take('recolor', texts.recolor);
  take('shapeFill', texts.fill);
  take('shapeLine', texts.stroke);
  take('alignLeft', texts.alignLeft);
  take('alignCenter', texts.alignCenter);
  take('alignRight', texts.alignRight);
  take('forward', texts.forward);
  take('backward', texts.backward);
  take('filterNone', texts.filterNone);
  take('filterGray', texts.filterGray);
  take('filterSepia', texts.filterSepia);
  take('brightness', texts.brightness);
  take('contrast', texts.contrast);
  take('nothingSelected', texts.selectLayer);
  take('panelMissing', texts.toolNotAllowed);
  return merged;
}

type Prefixed<T> = { [K in keyof T & string as `ph${Capitalize<K>}`]: string };

/**
 * The same strings under `ph`-prefixed keys, ready to be spread into
 * `DESIGNER_EXTRA_TEXTS` by the desktop agent so a shop can override a phone
 * string the same way it overrides every other one.
 */
export const PHONE_TEXT_TABLE: Prefixed<PhoneTexts> = Object.fromEntries(
  Object.entries(PHONE_TEXTS).map(([key, value]) => [`ph${key[0]!.toUpperCase()}${key.slice(1)}`, value]),
) as Prefixed<PhoneTexts>;
